<template>
  <div class="multi-select" v-click-outside="onClickOutside" style="position: relative;">
    <v-input
      ref="multiSelectInput"
      :placeholder="placeholder"
      v-model="searchValue"
      @focus="open()"
      :disabled="disabled"
      :icon="icon"
      :icon-position="iconPosition"
      :label="label"
      class="multi-select__input"
      v-show="show"
    />
    <div v-show="!show" class="input-group multi-select__input-group">
      <i class="input-group__icon icon-show input-group__icon--right"></i>
      <div class="input-group__input text-color-space cursor-pointer" :class="disabled ? 'multi-select__input--disabled' : null">
        <span v-if="!selectedItems.length">
          {{placeholder}}
        </span>

        <a href="#" class="multi-select__input-activator" @click="disabled ? () => {} : open()"></a>

        <div class="multi-select__chip-container" style="margin: -0.5rem" v-show="selectedItems.length">
          <div v-for="(item, index) of selectedItems" :key="item[valueField]" class="chip-multi ma-05">
            <span class="d-flex align-center">
              <span class="chip-multi__text d-block" style="line-height: 1;">{{item[itemText]}}</span>
              <a @click="disabled ? () => {} : removeCheckedItem(index)" class="icon-close d-block chip-multi__icon" style="margin-top: .1rem;"></a>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="multi-select__dropdown" v-if="filteredItems.length > 0 && show">
      <ul class="multi-select__list" v-if="customSlot">
        <li
          v-for="(item, index) in filteredItems"
          :key="index"
          :class="`${toggleActiveClass(index)} ${listClass}`"
        >
          <v-checkbox v-model="computedChecked" :val="valueField ? item[valueField] : index" :key="index" label-class="multi-select__list-item-content">
            <!--
              @slot Use it for creating a custome list-element view
              @binding {object} of single item
            -->
            <slot name="item" :item="item"></slot>
          </v-checkbox>
        </li>
      </ul>
      <ul class="multi-select__list" v-else>
        <li
          v-for="(item, index) in filteredItems"
          :key="index"
          :class="`${toggleActiveClass(index)} ${listClass}`"
        >
          <v-checkbox v-model="computedChecked" :val="valueField ? item[valueField] : index" :key="index" label-class="multi-select__list-item-content">
              {{ itemText ? item[itemText] : item }}
          </v-checkbox>
        </li>
      </ul>
    </div>
    <v-modal
      v-model="showModal"
      full
      no-background
      animation="slide-up"
      unclosable
      no-padding
      no-header-padding
    >
      <template v-slot:footer>
        <v-button width="full" @click="close()" :disabled="!selectedItems.length" color="success">{{$t('components.MultiSelect.footer')}}</v-button>
      </template>

      <template v-slot:header>
        <modal-header-input @click="close()" :height="headerHeight" v-model="searchValue" autofocus></modal-header-input>
      </template>

      <ul class="multi-select__list" v-if="customSlot">
        <li
          v-for="(item, index) in filteredItems"
          :key="index"
          :class="`${toggleActiveClass(index)} ${listClass}`"
        >
          <v-checkbox v-model="computedChecked" :val="valueField ? item[valueField] : index" :key="index" label-class="multi-select__list-item-content">
            <!--
              @slot Use it for creating a custome list-element view
              @binding {object} of single item
            -->
            <slot name="item" :item="item"></slot>
          </v-checkbox>
        </li>
      </ul>
      <ul class="multi-select__list py-1" v-else>
        <li
          v-for="(item, index) in filteredItems"
          :key="index"
          :class="`${toggleActiveClass(index)} ${listClass}`"
          class="multi-select__list-item">
          <v-checkbox v-model="computedChecked" :val="valueField ? item[valueField] : index" :key="index" label-class="multi-select__list-item-content">
            {{ itemText ? item[itemText] : item }}
          </v-checkbox>
        </li>
      </ul>
    </v-modal>
  </div>
</template>

<script>
import vInput from '@/ui/controls/vInput'
import vCheckbox from '@/ui/controls/vCheckbox'
import vButton from '@/ui/controls/vButton'

import vModal from '@/ui/modals/vModal'
import ModalHeaderInput from '@/ui/modals/ModalHeaderInput'

import screen from '@/mixins/screen'

import ClickOutside from '@/directives/clickOutside'

function getInitialMSHeaderHeight () {
  return 65 + parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat'))
}

/**
 * @displayName MultiSelect
 */

export default {
  name: 'multiselect',
  directives: {
    ClickOutside
  },
  data () {
    return {
      checked: [],
      localValue: '',
      searchValue: '',
      // selectedItemIndex: 0,
      filteredItemIndex: null,
      show: false,
      changed: 0,
      stopEmitting: false,

      showModal: false,
      headerHeight: getInitialMSHeaderHeight()
    }
  },
  mixins: [screen],
  props: {
    placeholder: {
      type: String,
      default: 'Start typing...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * @model
     */
    value: {
      type: Array,
      default: () => []
    },
    valueField: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    /**
     * You may specify "async" type for making requests to server
     * @values sync, async
     */
    type: {
      type: String,
      default: 'sync'
    },
    /**
     * Specify if you are going to use a custom slot
     * @values true, false
     */
    customSlot: {
      type: Boolean,
      default: false
    },
    /**
     * An array of items for filtering
     * @values [String], [{Object}]
     */
    items: {
      type: Array,
      default: () => []
    },
    /**
     * A field name with text if you use objects for items
     */
    itemText: {
      type: String,
      default: null
    },
    /**
     * Changes class name of a selected option
     */
    activeClass: {
      type: String,
      default: 'multi-select__list-item--active'
    },
    /**
     * Changes class name of list-items <li>
     */
    listClass: {
      type: String,
      default: 'multi-select__list-item'
    },
    /**
     * A class name of the icon from icon font, ex. "icon-search"
     */
    icon: {
      type: String,
      default: 'icon-arrow-down'
    },
    /**
     * @values left, right
     */
    iconPosition: {
      type: String,
      default: 'right'
    },
    /**
     * @values icon, button
     */
    iconType: {
      type: String,
      default: 'icon'
    }
  },
  components: {
    vInput,
    vModal,
    vCheckbox,
    vButton,
    ModalHeaderInput
  },
  computed: {
    selectedItems () {
      return this.value
    },
    filteredItems () {
      if (this.searchValue.length > 0) {
        return this.items.filter(item => {
          const str = this.itemText ? item[this.itemText] : item

          return str.toLowerCase().match(this.searchValue.toLowerCase())
        })
      }

      return this.items
    },
    computedChecked: {
      get () {
        return this.value.map((v, index) => this.valueField ? v[this.valueField] : index)
      },
      set (vals) {
        this.$emit('input',
          vals.map((v, index) =>
            this.valueField ? this.items.find(i => i[this.valueField] === v) : this.items[index])
        )
      }
    }
  },
  methods: {
    removeCheckedItem (index) {
      const newChecked = this.computedChecked
      newChecked.splice(index, 1)

      this.computedChecked = newChecked
    },
    close () {
      this.show = false
      this.showModal = false
    },
    open () {
      this.searchValue = ''
      if (this.screen.breakPoint.tabPort) {
        this.show = true
        const multiSelectInput = this.$refs.multiSelectInput.$refs.vInput
        this.$nextTick(() => {
          multiSelectInput.focus()
        })
        document.addEventListener('keydown', this.closeOnEsc)
      } else {
        this.showModal = true
        // this.$nextTick(() => {
        //   setTimeout(() => {
        //     this.$refs.multiSelectModalInput.focus()
        //   }, 50)
        // })
      }
    },
    closeOnEsc (event) {
      if (event.keyCode === 27) {
        this.close()
      }
    },
    onClickOutside () {
      if (this.screen.breakPoint.tabPort) {
        this.close()
      }
    },
    toggleActiveClass (currIndex) {
      if (currIndex === this.filteredItemIndex) return this.activeClass

      return ''
    },
    down () {
      if (this.filteredItemIndex === null) {
        this.filteredItemIndex = 0
      } else if (this.filteredItemIndex < this.filteredItems.length - 1) {
        this.filteredItemIndex++
      }

      this.open()
    },
    up () {
      if (this.filteredItemIndex > 0) {
        this.filteredItemIndex--
      } else {
        this.filteredItemIndex = null
      }
    },
    computeHeights () {
      // const header = document.querySelector('.modal-header-input')
      // if (header) {
      //   this.headerHeight = header.clientHeight + parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat'))
      // }
    }
  },
  mounted () {},
  watch: {
    show (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.computeHeights()
        })
      }
      /**
       * Events triggers when the dropdown shows or hides
       * @property {Boolean}
       */
      if (newVal === false) {
        document.removeEventListener('keydown', this.closeOnEsc)
      } else {
        document.addEventListener('keydown', this.closeOnEsc)
      }
      this.$emit('show', newVal)
    },

    'screen.height' () {
      this.computeHeights()
    },

    changed () {
      // This need to close dropdown just on change (setValue)
      this.close()
    }
  }
}
</script>

<style lang="scss">
  .multi-select {
    &__input-group {
      display: block;
    }

    &__input {
      &--disabled {
        background-color: $color-grey-extra-light;
      }
    }

    &__dropdown {
      max-height: 30rem;
      overflow-y: auto;
      position: absolute;
      left: 0;
      width: 100%;
      margin-top: -2px;
      border-radius: 0 0 3px 3px;
      border: 1px $color-grey-light solid;
      background-color: $color-white;
      box-shadow: $elevation;
      z-index: 99;
    }

    &__input-activator {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: block;
      cursor: text;
    }

    &__list-item {
      cursor: pointer;
      display: block;

      &--active > * {
        background-color: darken($color-grey-lighten, 1%);
      }
    }

    &__list-item {
      /*<!--&:not(:last-child) {-->*/
      /*<!--  border-bottom: 1px $color-grey-lighten solid;-->*/
      /*<!--}-->*/
    }

    &__list-item-content {
      padding: 1.5rem 1rem;

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          background-color: darken($color-grey-lighten, 1%);
        }
      }

      &:active {
        background-color: darken($color-grey-lighten, 1%);
      }
    }

    &__modal-header {
      width: 100%;
      height: 100%;
    }

    &__mobile-input {
      border: none;
      height: 100%;
      width: 100%;

      &:focus {
        border: none;
        outline: none;
      }
    }
  }

  .chip-multi {
    display: inline-block;
    padding: .3rem .5rem .45rem 1rem;
    color: $color-white;
    background-color: $color-secondary;
    border-radius: $border-radius-large;
    font-size: 1.2rem;

    &__icon {
      margin-left: .8rem;
      padding: .06rem .1rem;
      border-radius: 50%;
      transition: all .2s;
      position: relative;
      z-index: 1;

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          background-color: rgba($color-white, 0.2);
        }
      }
    }
  }
</style>
